import React, { useState, useEffect } from "react";

interface CredentialRow {
  readonly initials: string;
  readonly name: string;
  readonly credentialLabel: string;
  readonly status: CredentialStatus;
}

enum CredentialStatus {
  VERIFIED = "Verified",
  EXPIRING_SOON = "Expiring Soon",
  EXPIRED = "Expired",
  PENDING = "Pending",
}

const STATUS_STYLE: Record<
  CredentialStatus,
  { readonly color: string; readonly bg: string }
> = {
  [CredentialStatus.VERIFIED]:      { color: "#2563eb", bg: "rgba(37,99,235,0.09)" },
  [CredentialStatus.EXPIRING_SOON]: { color: "#d97706", bg: "rgba(217,119,6,0.09)" },
  [CredentialStatus.EXPIRED]:       { color: "#dc2626", bg: "rgba(220,38,38,0.09)" },
  [CredentialStatus.PENDING]:       { color: "#94a3b8", bg: "rgba(148,163,184,0.13)" },
};

const CREDENTIALS: readonly CredentialRow[] = [
  { initials: "MS", name: "Maria Santos",   credentialLabel: "RN License",       status: CredentialStatus.VERIFIED },
  { initials: "JO", name: "James Okafor",   credentialLabel: "CPR Certification", status: CredentialStatus.EXPIRING_SOON },
  { initials: "PN", name: "Priya Nair",     credentialLabel: "PSW Certificate",   status: CredentialStatus.VERIFIED },
  { initials: "DL", name: "Derek Liu",      credentialLabel: "First Aid Level C", status: CredentialStatus.EXPIRED },
  { initials: "AC", name: "Adaeze Chukwu",  credentialLabel: "TB Screening",      status: CredentialStatus.PENDING },
];

const TOTAL: number = CREDENTIALS.length;

type SlotName = "front" | "mid" | "back" | "exit";

interface SlotStyle {
  readonly transform: string;
  readonly opacity: number;
  readonly zIndex: number;
}

const SLOT: Record<SlotName, SlotStyle> = {
  front: { transform: "translateY(0px)   scale(1)",     opacity: 1,    zIndex: 4 },
  mid:   { transform: "translateY(18px)  scale(0.95)",  opacity: 0.55, zIndex: 3 },
  back:  { transform: "translateY(32px)  scale(0.905)", opacity: 0.22, zIndex: 2 },
  exit:  { transform: "translateY(-90px) scale(0.96)",  opacity: 0,    zIndex: 5 },
};

const getSlot = (offset: number, animating: boolean): SlotName => {
  if (animating) {
    if (offset === 0) return "exit";
    if (offset === 1) return "front";
    if (offset === 2) return "mid";
    return "back";
  }
  if (offset === 0) return "front";
  if (offset === 1) return "mid";
  return "back";
};

interface CardProps {
  readonly row: CredentialRow;
  readonly slot: SlotName;
}

const CredentialCard = ({ row, slot }: CardProps): React.ReactElement => {
  const isFront: boolean = slot === "front";
  const s: { readonly color: string; readonly bg: string } = STATUS_STYLE[row.status];
  const slotStyle: SlotStyle = SLOT[slot];

  return (
    <div
      style={{
        position: "absolute",
        width: "100%",
        ...slotStyle,
        transition: "transform 0.62s cubic-bezier(0.22,1,0.36,1), opacity 0.62s ease",
        transformOrigin: "bottom center",
        willChange: "transform, opacity",
      }}
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: 16,
          padding: "16px 18px",
          border: `1.5px solid ${isFront ? "#e8ecf2" : "#f0f2f6"}`,
          transition: "border-color 0.5s ease, box-shadow 0.5s ease",
          boxShadow: isFront
            ? "0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)"
            : "none",
        }}
      >
        {/* Top row: avatar + name + badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>

          {/* Avatar */}
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              flexShrink: 0,
              background: isFront ? s.bg : "var(--launch-colour2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 700,
              fontSize: 12,
              color: isFront ? s.color : "#b0bac9",
              transition: "background 0.5s ease, color 0.5s ease",
            }}
          >
            {row.initials}
          </div>

          {/* Name + label */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                fontSize: 13.5,
                color: "#0f172a",
                letterSpacing: -0.25,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {row.name}
            </div>
            <div
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 11.5,
                color: "#94a3b8",
                marginTop: 2,
              }}
            >
              {row.credentialLabel}
            </div>
          </div>

          {/* Status badge */}
          <div
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 11,
              fontWeight: 600,
              color: isFront ? s.color : "#b0bac9",
              background: isFront ? s.bg : "var(--launch-colour2)",
              padding: "4px 11px",
              borderRadius: 99,
              letterSpacing: 0.15,
              whiteSpace: "nowrap",
              flexShrink: 0,
              transition: "color 0.5s ease, background 0.5s ease",
            }}
          >
            {row.status}
          </div>
        </div>

        {/* Divider + credential detail row */}
        <div
          style={{
            marginTop: 13,
            paddingTop: 11,
            borderTop: `1px solid ${isFront ? "#f0f2f6" : "#f5f5f7"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            transition: "border-color 0.5s ease",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <div
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: isFront ? s.color : "#d1d5db",
                flexShrink: 0,
                transition: "background 0.5s ease",
              }}
            />
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 11.5,
                color: isFront ? "#64748b" : "#b0bac9",
                transition: "color 0.5s ease",
              }}
            >
              Credential on file
            </span>
          </div>
          <span
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 11,
              color: "#c8d0da",
            }}
          >
            Healthcare · PSW
          </span>
        </div>
      </div>
    </div>
  );
};

const CredentialTracker = (): React.ReactElement => {
  const [current, setCurrent] = useState<number>(0);
  const [animating, setAnimating] = useState<boolean>(false);

  useEffect((): (() => void) => {
    const interval: ReturnType<typeof setInterval> = setInterval((): void => {
      setAnimating(true);
      setTimeout((): void => {
        setCurrent((c: number): number => (c + 1) % TOTAL);
        setAnimating(false);
      }, 640);
    }, 2800);
    return (): void => clearInterval(interval);
  }, []);

  const visibleCards: readonly { readonly idx: number; readonly offset: number }[] = [
    { idx: current % TOTAL,           offset: 0 },
    { idx: (current + 1) % TOTAL,     offset: 1 },
    { idx: (current + 2) % TOTAL,     offset: 2 },
    ...(animating ? [{ idx: (current + 3) % TOTAL, offset: 3 }] : []),
  ];

  return (
    <div
      style={{
        width: "100%",
        height: "calc(100% - 100px)",
        background: "#ffffff",
        borderRadius: 18,
        padding: "20px 18px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        transform: "scale(0.90)",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: 118,
        }}
      >
        {visibleCards.map(
          ({ idx, offset }: { readonly idx: number; readonly offset: number }): React.ReactElement => (
            <CredentialCard
              key={idx}
              row={CREDENTIALS[idx]}
              slot={getSlot(offset, animating)}
            />
          )
        )}
      </div>
    </div>
  );
};

export default CredentialTracker;
